import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Loader2, Search, Filter, User, Stethoscope, MapPin, Calendar, Clock,
  Euro, Pencil, XCircle, AlertCircle, Mail, Phone, X, Save, Flag,
} from "lucide-react";

const STATUS_LABELS = {
  pending: { label: "En attente", c: "bg-gray-500/20 text-gray-300" },
  matched: { label: "Attente soignant", c: "bg-amber-500/20 text-amber-400" },
  pending_payment: { label: "À payer", c: "bg-[#00E073]/20 text-[#00E073]" },
  pending_nurse_validation: { label: "Attente infirmier", c: "bg-amber-500/20 text-amber-400" },
  confirmed: { label: "Confirmé", c: "bg-emerald-500/20 text-emerald-400" },
  completed_by_nurse: { label: "À valider (patient)", c: "bg-blue-500/20 text-blue-400" },
  completed: { label: "Terminé", c: "bg-green-500/20 text-green-400" },
  cancelled: { label: "Annulé", c: "bg-red-500/20 text-red-400" },
  refused: { label: "Refusé", c: "bg-red-500/20 text-red-400" },
  alternative_proposed: { label: "Créneau proposé", c: "bg-purple-500/20 text-purple-400" },
  disputed: { label: "Litige", c: "bg-orange-500/20 text-orange-400" },
};

const STATUS_OPTIONS = Object.keys(STATUS_LABELS);

const AdminCareRequests = ({ token }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [busy, setBusy] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("search", search.trim());
      const res = await fetch(`${API}/admin/care-requests?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setRequests(data.requests || []);
    } catch (e) {
      toast.error("Erreur de chargement des demandes");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, token]);

  useEffect(() => {
    const t = setTimeout(fetchRequests, 300);
    return () => clearTimeout(t);
  }, [fetchRequests]);

  const handleCancel = async () => {
    if (cancelReason.trim().length < 5) {
      toast.error("Merci d'indiquer un motif (5 caractères min).");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/care-requests/${cancelModal.request_id}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: cancelReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur");
      toast.success(data.refunded ? "Demande annulée et patient remboursé" : "Demande annulée");
      setCancelModal(null);
      setCancelReason("");
      fetchRequests();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (r) => {
    setEditForm({
      date: r.date || "", time: r.time || "", address: r.address || "",
      city: r.city || "", postal_code: r.postal_code || "", status: r.status,
    });
    setEditModal(r);
  };

  const handleEdit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/care-requests/${editModal.request_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur");
      toast.success("Demande mise à jour");
      setEditModal(null);
      fetchRequests();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-dark p-6" data-testid="admin-care-requests">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-white">Console Support — Demandes d'intervention</h2>
          <p className="text-sm text-[#94A3B8]">{requests.length} demande(s) — annulez ou modifiez si nécessaire</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Patient, infirmier, ville, ID..."
              className="input-dark pl-9 w-64"
              data-testid="admin-cr-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#0D1C32] border border-[#2A3A4D] text-white rounded-lg px-3 text-sm outline-none focus:border-[#00E073]"
            data-testid="admin-cr-status-filter"
          >
            <option value="all">Tous les statuts</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s].label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#00E073] animate-spin" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-[#64748B]">
          <Filter className="w-10 h-10 mx-auto mb-3 opacity-50" />
          Aucune demande trouvée
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => {
            const st = STATUS_LABELS[r.status] || STATUS_LABELS.pending;
            const canCancel = !["cancelled"].includes(r.status);
            return (
              <div key={r.request_id} className="bg-[#0D1C32] rounded-xl p-4 border border-[#2A3A4D]" data-testid={`admin-cr-row-${r.request_id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${st.c}`} data-testid={`admin-cr-status-${r.request_id}`}>
                        {st.label}
                      </span>
                      <span className="text-xs text-[#64748B] font-mono">{r.request_id}</span>
                      {r.is_urgent && <span className="text-xs text-red-400 font-medium">URGENT</span>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#94A3B8]">
                      <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {r.date} <Clock className="w-4 h-4 ml-1" /> {r.time}{r.duration_minutes ? ` (${r.duration_minutes} min)` : ""}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {r.address ? `${r.address}, ` : ""}{r.postal_code} {r.city}</span>
                      <span className="flex items-center gap-1.5 text-white"><User className="w-4 h-4 text-[#00E073]" /> {r.patient_name || "—"}</span>
                      <span className="flex items-center gap-1.5 text-white"><Stethoscope className="w-4 h-4 text-[#28CAD4]" /> {r.nurse_name || "Aucun infirmier"}</span>
                      {r.patient_nationality && (
                        <span className="flex items-center gap-1.5 text-xs" data-testid={`admin-cr-nationality-${r.request_id}`}><Flag className="w-3 h-3 text-[#00E073]" /> Nationalité : {r.patient_nationality}</span>
                      )}
                      {(r.patient_email || r.patient_phone) && (
                        <span className="flex items-center gap-1.5 text-xs"><Mail className="w-3 h-3" /> {r.patient_email} {r.patient_phone && <><Phone className="w-3 h-3 ml-1" /> {r.patient_phone}</>}</span>
                      )}
                      {(r.nurse_email || r.nurse_phone) && (
                        <span className="flex items-center gap-1.5 text-xs"><Mail className="w-3 h-3" /> {r.nurse_email} {r.nurse_phone && <><Phone className="w-3 h-3 ml-1" /> {r.nurse_phone}</>}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#64748B] flex-wrap">
                      {r.total_amount > 0 && <span className="flex items-center gap-1 text-[#00E073]"><Euro className="w-3 h-3" /> Total {r.total_amount} € (infirmier {r.nurse_tarif ?? "—"} € · frais {r.platform_fee ?? "—"} € · TVA {r.platform_vat ?? "—"} €)</span>}
                      {r.payment_status && <span>Paiement: {r.payment_status}</span>}
                      {r.escrow_status && <span>Séquestre: {r.escrow_status}</span>}
                    </div>
                    {r.cancellation_reason && (
                      <p className="text-xs text-red-400 italic">Motif annulation : {r.cancellation_reason}</p>
                    )}
                  </div>
                  <div className="flex lg:flex-col gap-2">
                    <Button
                      size="sm" variant="outline"
                      className="border-[#00E073]/40 text-[#00E073] hover:bg-[#00E073]/10"
                      onClick={() => openEdit(r)}
                      data-testid={`admin-cr-edit-${r.request_id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Modifier
                    </Button>
                    {canCancel && (
                      <Button
                        size="sm" variant="outline"
                        className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                        onClick={() => { setCancelReason(""); setCancelModal(r); }}
                        data-testid={`admin-cr-cancel-${r.request_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Annuler
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cancel modal */}
      {cancelModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="admin-cr-cancel-modal">
          <div className="bg-[#1F2A38] border border-[#2A3A4D] rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <h3 className="text-lg font-bold text-white">Annuler la demande</h3>
            </div>
            <p className="text-sm text-[#94A3B8] mb-4">
              {cancelModal.escrow_status === "held"
                ? "Les fonds sont en séquestre : le patient sera automatiquement remboursé."
                : "Aucun paiement en séquestre à rembourser."} Indiquez le motif.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Motif de l'annulation (support)..."
              className="w-full bg-[#0D1C32] border border-[#2A3A4D] rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:border-red-400"
              rows={3}
              data-testid="admin-cr-cancel-reason"
            />
            <div className="flex gap-3 mt-4">
              <Button variant="outline" className="flex-1 border-[#2A3A4D] text-[#94A3B8]" onClick={() => setCancelModal(null)}>Fermer</Button>
              <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white" onClick={handleCancel} disabled={busy || cancelReason.trim().length < 5} data-testid="admin-cr-cancel-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                Confirmer l'annulation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="admin-cr-edit-modal">
          <div className="bg-[#1F2A38] border border-[#2A3A4D] rounded-2xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Pencil className="w-5 h-5 text-[#00E073]" /> Modifier la demande</h3>
              <button onClick={() => setEditModal(null)} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Date</Label>
                <Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="input-dark" data-testid="admin-cr-edit-date" />
              </div>
              <div>
                <Label className="form-label">Heure</Label>
                <Input type="time" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} className="input-dark" data-testid="admin-cr-edit-time" />
              </div>
              <div className="col-span-2">
                <Label className="form-label">Adresse</Label>
                <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="input-dark" data-testid="admin-cr-edit-address" />
              </div>
              <div>
                <Label className="form-label">Code postal</Label>
                <Input value={editForm.postal_code} onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })} className="input-dark" />
              </div>
              <div>
                <Label className="form-label">Ville</Label>
                <Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="input-dark" />
              </div>
              <div className="col-span-2">
                <Label className="form-label">Statut</Label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full bg-[#0D1C32] border border-[#2A3A4D] text-white rounded-lg p-2.5 text-sm outline-none focus:border-[#00E073]"
                  data-testid="admin-cr-edit-status"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s].label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <Button variant="outline" className="flex-1 border-[#2A3A4D] text-[#94A3B8]" onClick={() => setEditModal(null)}>Annuler</Button>
              <Button className="btn-primary flex-1" onClick={handleEdit} disabled={busy} data-testid="admin-cr-edit-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCareRequests;
